import React, { useState, useEffect } from "react";
import ChatTrackerNav from "./ChatTrackerNav";

const ChatTrackerAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Mock data for analytics
        const mockData = {
          totalConversations: 2456,
          activeConversations: 534,
          averageResolutionTime: "3.2 hours",
          topIntents: [
            { intent: "Account Access", count: 387, percentage: 28 },
            { intent: "Technical Support", count: 256, percentage: 18 },
            { intent: "Billing Inquiry", count: 215, percentage: 15 },
            { intent: "Product Information", count: 187, percentage: 13 },
            { intent: "Other", count: 365, percentage: 26 },
          ],
          sentimentBreakdown: {
            positive: 45,
            neutral: 32,
            negative: 23,
          },
          dailyConversations: [
            { date: "2023-05-01", count: 120 },
            { date: "2023-05-02", count: 145 },
            { date: "2023-05-03", count: 132 },
            { date: "2023-05-04", count: 167 },
            { date: "2023-05-05", count: 189 },
            { date: "2023-05-06", count: 156 },
            { date: "2023-05-07", count: 142 },
          ],
        };

        setAnalyticsData(mockData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching analytics data:", error);
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [timeRange]);

  const renderLoading = () => (
    <div className="flex justify-center items-center py-10">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
    </div>
  );

  // Helper function to calculate the width of the bar based on percentage
  const getBarWidth = (percentage) => {
    return `${percentage}%`;
  };

  const renderTopIntents = () => {
    if (!analyticsData || !analyticsData.topIntents) return null;

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Top Conversation Intents
        </h3>
        <div className="space-y-4">
          {analyticsData.topIntents.map((intent, index) => (
            <div key={index}>
              <div className="flex justify-between text-sm font-medium text-gray-900">
                <span>{intent.intent}</span>
                <span>
                  {intent.count} ({intent.percentage}%)
                </span>
              </div>
              <div className="mt-1 relative pt-1">
                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                  <div
                    style={{ width: getBarWidth(intent.percentage) }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSentimentBreakdown = () => {
    if (!analyticsData || !analyticsData.sentimentBreakdown) return null;

    const { positive, neutral, negative } = analyticsData.sentimentBreakdown;

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Sentiment Analysis
        </h3>
        <div className="relative pt-1">
          <div className="flex h-4 overflow-hidden text-xs bg-gray-200 rounded">
            <div
              style={{ width: `${positive}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
            ></div>
            <div
              style={{ width: `${neutral}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gray-500"
            ></div>
            <div
              style={{ width: `${negative}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-red-500"
            ></div>
          </div>
        </div>
        <div className="flex justify-between mt-2 text-xs">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
            <span>Positive ({positive}%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-gray-500 mr-1"></div>
            <span>Neutral ({neutral}%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
            <span>Negative ({negative}%)</span>
          </div>
        </div>
      </div>
    );
  };

  const renderDailyConversations = () => {
    if (!analyticsData || !analyticsData.dailyConversations) return null;

    const maxCount = Math.max(
      ...analyticsData.dailyConversations.map((day) => day.count)
    );

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Daily Conversation Volume
        </h3>
        <div className="h-64">
          <div className="flex h-56 space-x-2">
            {analyticsData.dailyConversations.map((day, index) => {
              const height = (day.count / maxCount) * 100;
              const date = new Date(day.date);
              const dayName = date.toLocaleDateString("en-US", {
                weekday: "short",
              });

              return (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div className="flex-1 w-full flex items-end">
                    <div
                      className="w-full bg-blue-500 rounded-t"
                      style={{ height: `${height}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{dayName}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="relative flex w-full border-l"
      style={{
        background:
          "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
      }}
    >
      <div className="w-full flex flex-col">
        <ChatTrackerNav />

        <div className="w-full flex flex-col px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex justify-between items-center mb-4 gap-3">
              <h2 className="font-SFPro font-bold text-2xl text-gray-900">
                Analytics Dashboard
              </h2>
              <select
                className="block border border-gray-200/80 bg-white/70 backdrop-blur rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 text-sm px-3 py-2 text-gray-700"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="365d">Last Year</option>
              </select>
            </div>

            {loading ? (
              renderLoading()
            ) : (
              <>
                {/* Stats Cards (DashboardHome-style) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                  <div
                    className="w-full rounded-xl px-5 py-5 flex flex-col gap-3 border border-blue-100"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                    }}
                  >
                    <dt className="font-inter text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Conversations
                    </dt>
                    <dd className="font-inter text-4xl font-semibold text-gray-900">
                      {analyticsData?.totalConversations.toLocaleString()}
                    </dd>
                    <div className="text-xs text-gray-400 font-inter border-t border-blue-100 pt-2">
                      All conversations
                    </div>
                  </div>

                  <div
                    className="w-full rounded-xl px-5 py-5 flex flex-col gap-3 border border-green-100"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                    }}
                  >
                    <dt className="font-inter text-xs font-medium text-green-600 uppercase tracking-wider">
                      Active Conversations
                    </dt>
                    <dd className="font-inter text-4xl font-semibold text-green-600">
                      {analyticsData?.activeConversations.toLocaleString()}
                    </dd>
                    <div className="text-xs text-gray-400 font-inter border-t border-green-100 pt-2">
                      Currently active
                    </div>
                  </div>

                  <div
                    className="w-full rounded-xl px-5 py-5 flex flex-col gap-3 border border-violet-100"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #eef2ff 100%)",
                    }}
                  >
                    <dt className="font-inter text-xs font-medium text-violet-600 uppercase tracking-wider">
                      Avg. Resolution Time
                    </dt>
                    <dd className="font-inter text-4xl font-semibold text-violet-600">
                      {analyticsData?.averageResolutionTime}
                    </dd>
                    <div className="text-xs text-gray-400 font-inter border-t border-violet-100 pt-2">
                      Average time to resolve
                    </div>
                  </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {renderTopIntents()}
                  {renderSentimentBreakdown()}
                </div>

                <div className="mt-6">{renderDailyConversations()}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatTrackerAnalytics;
